import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { ExtendedTask, TaskSortType } from "@/components/Task/types"
import { useConfigStore } from "./useConfigStore"

type AccountId = string;
type TasklistId = string;

interface TaskSortStore {
  tasksOrders: Record<AccountId, Record<TasklistId, ExtendedTask[]>>
  tasklistSortTypes: Record<AccountId, Record<TasklistId, TaskSortType>>
  setTasksOrders: (accountId: AccountId, tasklistId: TasklistId, tasksOrders: ExtendedTask[]) => void
  getTasksOrders: (accountId: AccountId, tasklistId: TasklistId) => ExtendedTask[]
  sortTasksBySortType: (accountId: AccountId, tasklistId: TasklistId, sortType: TaskSortType) => void
  getTasklistSortType: (accountId: AccountId, tasklistId: TasklistId) => TaskSortType
  setTasklistSortType: (accountId: AccountId, tasklistId: TasklistId, sortType: TaskSortType) => void
}

const sortFunctions: Record<TaskSortType, (a: ExtendedTask, b: ExtendedTask) => number> = {
  "position": (a, b) => (a.position && b.position) ? a.position.localeCompare(b.position) : 0,
  "dueDate": (a, b) => (a.due && b.due ? new Date(a.due).getTime() - new Date(b.due).getTime() : 0),
  "alphabetical": (a, b) => (a.title && b.title ? a.title.localeCompare(b.title) : 0),
}

export const useTaskSortStore = create<TaskSortStore>()(
  devtools(
    (set, get) => ({
      tasksOrders: {},
      tasklistSortTypes: {},
      setTasksOrders: (accountId: AccountId, tasklistId: TasklistId, tasksOrders: ExtendedTask[]): void => {
        return set((state) => ({ 
          tasksOrders: { ...state.tasksOrders, [accountId]: { ...state.tasksOrders[accountId], [tasklistId]: tasksOrders } } 
        }))
      },
      sortTasksBySortType: (accountId: AccountId, tasklistId: TasklistId, sortType: TaskSortType): void => {
        // Also update the sort type when sorting
        set((state) => ({
          tasklistSortTypes: { 
            ...state.tasklistSortTypes, 
            [accountId]: { 
              ...state.tasklistSortTypes[accountId], 
              [tasklistId]: sortType 
            } 
          }
        }));
        
        return set((state) => ({
          tasksOrders: { 
            ...state.tasksOrders, 
            [accountId]: { 
              ...state.tasksOrders[accountId], 
              [tasklistId]: state.tasksOrders[accountId][tasklistId].sort(sortFunctions[sortType]) 
            } 
          }
        }))
      },
      getTasksOrders: (accountId: AccountId, tasklistId: TasklistId): ExtendedTask[] => {
        if (!get().tasksOrders[accountId]) {
          return []
        }
        if (!get().tasksOrders[accountId][tasklistId]) {
          return []
        }
        return get().tasksOrders[accountId][tasklistId]
      },
      getTasklistSortType: (accountId: AccountId, tasklistId: TasklistId): TaskSortType => {
        if (!get().tasklistSortTypes[accountId]) {
          return "position" // Default sort type
        }
        if (!get().tasklistSortTypes[accountId][tasklistId]) {
          return "position" // Default sort type
        }
        return get().tasklistSortTypes[accountId][tasklistId]
      },
      setTasklistSortType: (accountId: AccountId, tasklistId: TasklistId, sortType: TaskSortType): void => {
        set((state) => ({
          tasklistSortTypes: { 
            ...state.tasklistSortTypes, 
            [accountId]: { 
              ...state.tasklistSortTypes[accountId], 
              [tasklistId]: sortType 
            } 
          }
        }))
        useConfigStore.getState().setTasklistSortType(accountId, tasklistId, sortType)
      }
    }),
    { name: "TaskSortStore", enabled: process.env.NODE_ENV !== "production" }
  )
)
